'use client';

import { PieChart, Pie, Cell } from 'recharts';
import { cn } from '../lib/utils';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  type ChartConfig,
} from '../chart';

export interface DonutSlice {
  key: string;
  label: string;
  value: number;
  color: string;
}

interface DonutChartProps {
  data: DonutSlice[];
  centerText?: string;
  centerSubText?: string;
  showLegend?: boolean;
  height?: number;
  className?: string;
}

export function DonutChart({
  data,
  centerText = '',
  centerSubText = '',
  showLegend = true,
  height,
  className,
}: DonutChartProps) {
  const config: ChartConfig = Object.fromEntries(
    data.map((d) => [d.key, { label: d.label, color: d.color }]),
  );

  const LegendContent = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '4px 16px',
        paddingTop: 8,
      }}
    >
      {data.map((slice) => (
        <div
          key={slice.key}
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
        >
          <span
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: 2,
              background: `var(--color-${slice.key})`,
              flexShrink: 0,
            }}
          />
          <span className="text-[var(--color-text-secondary,#71717a)]">{slice.label}</span>
          <span className="font-bold text-[var(--color-text-primary,#18181b)]">{slice.value}</span>
        </div>
      ))}
    </div>
  );

  return (
    <ChartContainer
      config={config}
      className={cn('w-full aspect-auto!', className)}
      style={height ? { height } : undefined}
    >
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="key"
          cx="50%"
          cy="50%"
          innerRadius="55%"
          outerRadius="80%"
          startAngle={90}
          endAngle={-270}
          paddingAngle={data.length > 1 ? 2 : 0}
        >
          {data.map((entry) => (
            <Cell key={entry.key} fill={`var(--color-${entry.key})`} />
          ))}
        </Pie>
        {(centerText || centerSubText) && (
          <text x="50%" y="50%" textAnchor="middle">
            {centerText && (
              <tspan
                x="50%"
                y="50%"
                dy={centerSubText ? '-0.6em' : '0.35em'}
                className="fill-foreground text-2xl font-bold"
              >
                {centerText}
              </tspan>
            )}
            {centerSubText && (
              <tspan x="50%" y="50%" dy="1.2em" className="fill-muted-foreground text-xs">
                {centerSubText}
              </tspan>
            )}
          </text>
        )}
        <ChartTooltip content={<ChartTooltipContent nameKey="key" />} />
        {showLegend && <ChartLegend content={() => <LegendContent />} />}
      </PieChart>
    </ChartContainer>
  );
}
